import React, { useEffect, useState, useCallback } from 'react';
import { api, formatApiError } from '../lib/api';
import { toast } from 'sonner';
import {
  FileSignature, Eye, Download, Printer, Send, Ban, RefreshCw,
  ChevronDown, ChevronUp, ShieldCheck, X,
} from 'lucide-react';

const fmtDT = (iso) => (iso ? new Date(iso).toLocaleString() : '—');
const fmtD = (iso) => (iso ? new Date(iso).toLocaleDateString() : '—');

const STATUS_STYLES = {
  draft:      'bg-gray-100 text-gray-600',
  sent:       'bg-[#FBF1D9] text-[#8B6A3E]',
  viewed:     'bg-blue-50 text-blue-700',
  signed:     'bg-[#E7F0E4] text-[#3F6B45]',
  declined:   'bg-red-50 text-red-700',
  expired:    'bg-orange-50 text-orange-700',
  voided:     'bg-gray-100 text-gray-500 line-through',
  superseded: 'bg-gray-100 text-gray-500',
};

function StatusBadge({ row }) {
  const st = row.status;
  return (
    <span className="inline-flex flex-col items-start gap-0.5">
      <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded font-semibold ${STATUS_STYLES[st] || STATUS_STYLES.draft}`}
            data-testid={`agreement-status-${row.agreement_id}`}>
        {st}
      </span>
      {st === 'superseded' && row.superseded_by_label && (
        <span className="text-[9px] text-[hsl(var(--primary)/0.5)]">{row.superseded_by_label}</span>
      )}
    </span>
  );
}

const apiOrigin = () => api.defaults.baseURL?.replace(/\/api$/, '') || '';

function printPdf(url) {
  const iframe = document.createElement('iframe');
  iframe.style.display = 'none';
  iframe.src = url;
  iframe.onload = () => {
    try { iframe.contentWindow.print(); }
    catch { window.open(url, '_blank'); }
  };
  document.body.appendChild(iframe);
}

function AuditTrail({ audit }) {
  if (!audit?.length) {
    return <div className="px-4 py-3 text-[11px] text-[hsl(var(--primary)/0.5)]">No audit entries (legacy record — see platform audit log).</div>;
  }
  return (
    <ul className="divide-y divide-[hsl(var(--primary)/0.06)]" data-testid="agreement-audit-trail">
      {audit.slice().reverse().map((a, i) => (
        <li key={i} className="px-4 py-2 flex flex-wrap items-baseline gap-x-3 gap-y-0.5 text-[11px]">
          <span className="uppercase tracking-wider font-semibold text-[hsl(var(--primary))] w-24">{a.action}</span>
          <span className="text-[hsl(var(--primary)/0.7)]">
            {a.actor_name || a.actor_id || 'system'} ({a.actor_type})
          </span>
          <span className="text-[hsl(var(--primary)/0.55)] tabular-nums">{fmtDT(a.at)}</span>
          {a.ip && <span className="font-mono text-[10px] text-[hsl(var(--primary)/0.45)]">IP {a.ip}</span>}
          {a.details?.reason && <span className="text-[hsl(var(--primary)/0.6)] italic">"{a.details.reason}"</span>}
          {a.details?.which && <span className="text-[hsl(var(--primary)/0.5)]">({a.details.which} PDF)</span>}
        </li>
      ))}
    </ul>
  );
}

function SendAgreementModal({ user, hasSigned, onClose, onSent }) {
  const [presets, setPresets] = useState([]);
  const [presetKey, setPresetKey] = useState('');
  const [sending, setSending] = useState(false);

  useEffect(() => {
    api.get('/admin/agreement-presets')
      .then(({ data }) => setPresets((data.presets || []).filter((p) => {
        const allowed = (p.allowed_emails || []).map((e) => e.toLowerCase());
        return allowed.length === 0 || allowed.includes((user.email || '').toLowerCase());
      })))
      .catch(() => {});
  }, [user.email]);

  const send = async () => {
    setSending(true);
    try {
      const { data } = await api.post(`/admin/partners/${user.user_id}/agreements/send`,
        { preset_key: presetKey || null });
      toast.success(data.delivered
        ? `Agreement ${data.version_label} sent to ${user.email} for signature.`
        : 'Agreement created — email delivery failed, use Resend.');
      onSent();
      onClose();
    } catch (e) {
      toast.error(formatApiError(e, 'Could not send agreement.'));
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-start justify-center p-4 overflow-y-auto"
         data-testid="send-agreement-modal">
      <div className="bg-[hsl(var(--card))] rounded-2xl border border-[hsl(var(--primary)/0.15)] w-full max-w-lg mt-16 p-6">
        <div className="flex items-start justify-between">
          <h3 className="font-serif text-xl text-[hsl(var(--primary))]">
            Send {hasSigned ? 'updated agreement (amendment)' : 'agreement'} for signature
          </h3>
          <button onClick={onClose} data-testid="send-agreement-close"
                  className="p-1 text-[hsl(var(--primary)/0.6)] hover:text-[hsl(var(--primary))]"><X size={18} /></button>
        </div>
        <p className="text-[12px] text-[hsl(var(--primary)/0.65)] mt-2">
          To: <strong>{user.name}</strong> ({user.email}). The partner receives an email
          with the exact PDF and a secure signing link (expires in 30 days).
          {hasSigned && ' Their previously signed copy is preserved for legal records — this will be marked as an amendment requiring a new signature.'}
          {' '}Any unsigned agreements in flight are automatically voided and marked superseded.
        </p>
        <div className="mt-4 space-y-2">
          <label className={`flex items-start gap-2.5 rounded-lg border p-3 cursor-pointer ${!presetKey ? 'border-[hsl(var(--accent))] bg-[hsl(var(--accent)/0.06)]' : 'border-[hsl(var(--primary)/0.12)]'}`}>
            <input type="radio" name="preset" checked={!presetKey} onChange={() => setPresetKey('')}
                   data-testid="send-agreement-preset-standard" className="mt-0.5" />
            <span>
              <span className="block text-sm font-medium text-[hsl(var(--primary))]">Standard agreement (v1.1)</span>
              <span className="block text-[11px] text-[hsl(var(--primary)/0.55)]">The default Independent Partner Agreement.</span>
            </span>
          </label>
          {presets.map((p) => (
            <label key={p.key} className={`flex items-start gap-2.5 rounded-lg border p-3 cursor-pointer ${presetKey === p.key ? 'border-[hsl(var(--accent))] bg-[hsl(var(--accent)/0.06)]' : 'border-[hsl(var(--primary)/0.12)]'}`}>
              <input type="radio" name="preset" checked={presetKey === p.key} onChange={() => setPresetKey(p.key)}
                     data-testid={`send-agreement-preset-${p.key}`} className="mt-0.5" />
              <span>
                <span className="block text-sm font-medium text-[hsl(var(--primary))]">{p.label}</span>
                <span className="block text-[11px] text-[hsl(var(--primary)/0.55)]">
                  {p.clause_count} clauses · custom amendment{p.key === 'jbleim_v1' ? ' — locked to jbleim23@gmail.com only' : ''}
                </span>
              </span>
            </label>
          ))}
        </div>
        <div className="mt-5 flex flex-wrap items-center justify-between gap-2">
          <button
            type="button"
            onClick={() => window.open(`${apiOrigin()}/api/admin/partners/${user.user_id}/agreements/preview${presetKey ? `?preset_key=${presetKey}` : ''}`, '_blank')}
            data-testid="send-agreement-preview-btn"
            className="px-4 py-2 rounded-full border border-[hsl(var(--accent))] text-[hsl(var(--accent))] text-xs uppercase tracking-wider font-semibold"
          >
            Preview PDF first →
          </button>
          <div className="flex gap-2">
            <button onClick={onClose} className="px-4 py-2 rounded-full border border-[hsl(var(--primary)/0.2)] text-sm"
                    data-testid="send-agreement-cancel">Cancel</button>
            <button onClick={send} disabled={sending} data-testid="send-agreement-submit"
                    className="px-5 py-2 rounded-full bg-[hsl(var(--primary))] text-[hsl(var(--accent))] text-sm font-medium disabled:opacity-50">
              {sending ? 'Sending…' : 'Send for signature'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export function AgreementsPanel({ user }) {
  const [data, setData] = useState(null);
  const [expanded, setExpanded] = useState(null);
  const [showSend, setShowSend] = useState(false);
  const [voiding, setVoiding] = useState(null);
  const [voidReason, setVoidReason] = useState('');

  const load = useCallback(() => {
    api.get(`/admin/partners/${user.user_id}/agreements`)
      .then(({ data: d }) => setData(d))
      .catch((e) => toast.error(formatApiError(e, 'Could not load agreements.')));
  }, [user.user_id]);

  useEffect(() => { load(); }, [load]);

  if (!data) return null;
  const rows = data.rows || [];
  const hasSigned = rows.some((r) => r.status === 'signed');

  const pdfUrl = (row, which, download) => row.legacy
    ? `${apiOrigin()}/api/admin/partners/${user.user_id}/agreement.pdf`
    : `${apiOrigin()}/api/admin/agreements/${row.agreement_id}/pdf?which=${which}${download ? '&download=1' : ''}`;

  const resend = async (row) => {
    try {
      if (row.legacy) {
        await api.post(`/admin/partners/${user.user_id}/resend-agreement`);
      } else {
        await api.post(`/admin/agreements/${row.agreement_id}/resend`);
      }
      toast.success('Agreement re-sent.');
      load();
    } catch (e) {
      toast.error(formatApiError(e, 'Resend failed.'));
    }
  };

  const doVoid = async () => {
    try {
      await api.post(`/admin/agreements/${voiding}/void`, { reason: voidReason });
      toast.success('Agreement voided.');
      setVoiding(null); setVoidReason('');
      load();
    } catch (e) {
      toast.error(formatApiError(e, 'Void failed.'));
    }
  };

  return (
    <section className="mb-8 rounded-2xl border border-[hsl(var(--primary)/0.14)] bg-[hsl(var(--card))] overflow-hidden"
             data-testid="agreements-panel">
      <header className="px-6 py-4 border-b border-[hsl(var(--primary)/0.08)] flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <FileSignature size={18} className="text-[hsl(var(--accent))]" />
          <h3 className="font-serif text-xl text-[hsl(var(--primary))]">Agreements</h3>
          <span className="text-[10px] uppercase tracking-[0.18em] text-[hsl(var(--primary)/0.5)]">
            Contract management · full audit trail
          </span>
        </div>
        <button onClick={() => setShowSend(true)} data-testid="agreements-send-new-btn"
                className="flex items-center gap-1.5 px-4 py-2 rounded-full bg-[hsl(var(--primary))] text-[hsl(var(--accent))] text-xs font-semibold uppercase tracking-wider">
          <Send size={13} /> {hasSigned ? 'Send updated agreement' : 'Send agreement'}
        </button>
      </header>

      {rows.length === 0 ? (
        <div className="p-6 text-center text-[hsl(var(--primary)/0.55)] text-sm" data-testid="agreements-empty">
          No agreements on record for this partner yet.
        </div>
      ) : (
        <div className="divide-y divide-[hsl(var(--primary)/0.08)]">
          {rows.map((row) => {
            const canResend = ['sent', 'viewed'].includes(row.status) || (row.legacy && row.status === 'signed');
            const canVoid = ['sent', 'viewed', 'draft'].includes(row.status) && !row.legacy;
            const showSignedPdf = row.status === 'signed';
            const isOpen = expanded === row.agreement_id;
            return (
              <div key={row.agreement_id} data-testid={`agreement-row-${row.agreement_id}`}>
                <div className="px-6 py-3.5 flex flex-wrap items-center gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-[hsl(var(--primary))] text-sm">{row.version_label}</span>
                      {row.kind === 'amendment' && (
                        <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-[#FBF1D9] text-[#8B6A3E] font-semibold">Amendment</span>
                      )}
                      {row.legacy && (
                        <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-[hsl(var(--primary)/0.06)] text-[hsl(var(--primary)/0.6)] font-semibold">Original record</span>
                      )}
                      <StatusBadge row={row} />
                    </div>
                    <div className="text-[11px] text-[hsl(var(--primary)/0.55)] mt-0.5 flex flex-wrap gap-x-3">
                      {row.sent_at && <span>Sent {fmtD(row.sent_at)}</span>}
                      {row.viewed_at && <span>Viewed {fmtDT(row.viewed_at)}</span>}
                      {row.signed_at && (
                        <span className="text-[#3F6B45] font-medium">
                          Signed {fmtDT(row.signed_at)}{row.signature_full_name ? ` by ${row.signature_full_name}` : ''}
                        </span>
                      )}
                      {row.declined_at && <span className="text-red-700">Declined {fmtDT(row.declined_at)}</span>}
                      {row.status === 'expired' && row.expires_at && <span>Expired {fmtD(row.expires_at)}</span>}
                      {['sent', 'viewed'].includes(row.status) && row.expires_at && <span>Expires {fmtD(row.expires_at)}</span>}
                    </div>
                  </div>
                  <div className="flex items-center gap-1 flex-wrap">
                    <button onClick={() => window.open(pdfUrl(row, showSignedPdf && !row.legacy ? 'signed' : 'sent'), '_blank')}
                            title="View PDF" data-testid={`agreement-view-${row.agreement_id}`}
                            className="p-2 rounded-lg hover:bg-[hsl(var(--primary)/0.06)] text-[hsl(var(--primary)/0.7)]">
                      <Eye size={15} />
                    </button>
                    <button onClick={() => window.open(pdfUrl(row, showSignedPdf && !row.legacy ? 'signed' : 'sent', true), '_blank')}
                            title="Download PDF" data-testid={`agreement-download-${row.agreement_id}`}
                            className="p-2 rounded-lg hover:bg-[hsl(var(--primary)/0.06)] text-[hsl(var(--primary)/0.7)]">
                      <Download size={15} />
                    </button>
                    <button onClick={() => printPdf(pdfUrl(row, showSignedPdf && !row.legacy ? 'signed' : 'sent'))}
                            title="Print PDF" data-testid={`agreement-print-${row.agreement_id}`}
                            className="p-2 rounded-lg hover:bg-[hsl(var(--primary)/0.06)] text-[hsl(var(--primary)/0.7)]">
                      <Printer size={15} />
                    </button>
                    {showSignedPdf && !row.legacy && (
                      <button onClick={() => window.open(pdfUrl(row, 'sent'), '_blank')}
                              title="View the original PDF as sent (pre-signature)"
                              data-testid={`agreement-view-sent-${row.agreement_id}`}
                              className="px-2 py-1 rounded-lg hover:bg-[hsl(var(--primary)/0.06)] text-[10px] uppercase tracking-wider text-[hsl(var(--primary)/0.55)]">
                        As sent
                      </button>
                    )}
                    {canResend && (
                      <button onClick={() => resend(row)} title="Resend agreement email"
                              data-testid={`agreement-resend-${row.agreement_id}`}
                              className="p-2 rounded-lg hover:bg-[hsl(var(--primary)/0.06)] text-[hsl(var(--primary)/0.7)]">
                        <RefreshCw size={15} />
                      </button>
                    )}
                    {canVoid && (
                      <button onClick={() => setVoiding(row.agreement_id)} title="Void agreement"
                              data-testid={`agreement-void-${row.agreement_id}`}
                              className="p-2 rounded-lg hover:bg-red-50 text-red-600">
                        <Ban size={15} />
                      </button>
                    )}
                    <button onClick={() => setExpanded(isOpen ? null : row.agreement_id)}
                            title="Audit trail" data-testid={`agreement-audit-toggle-${row.agreement_id}`}
                            className="p-2 rounded-lg hover:bg-[hsl(var(--primary)/0.06)] text-[hsl(var(--primary)/0.7)]">
                      {isOpen ? <ChevronUp size={15} /> : <ChevronDown size={15} />}
                    </button>
                  </div>
                </div>
                {isOpen && (
                  <div className="bg-[hsl(var(--primary)/0.025)] border-t border-[hsl(var(--primary)/0.06)]">
                    <div className="px-4 pt-2.5 pb-1 flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em] text-[hsl(var(--primary)/0.5)] font-semibold">
                      <ShieldCheck size={11} /> Audit history
                      {row.pdf_sent_sha256 && (
                        <span className="ml-auto font-mono normal-case tracking-normal">sha256 {row.pdf_sent_sha256.slice(0, 16)}…</span>
                      )}
                    </div>
                    <AuditTrail audit={row.audit} />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {showSend && (
        <SendAgreementModal user={user} hasSigned={hasSigned}
                            onClose={() => setShowSend(false)} onSent={load} />
      )}

      {voiding && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-start justify-center p-4"
             data-testid="void-agreement-modal">
          <div className="bg-[hsl(var(--card))] rounded-2xl border border-[hsl(var(--primary)/0.15)] w-full max-w-md mt-24 p-6">
            <h3 className="font-serif text-xl text-[hsl(var(--primary))]">Void this agreement?</h3>
            <p className="text-[12px] text-[hsl(var(--primary)/0.65)] mt-2">
              The signing link will stop working and the agreement will be marked voided. This is recorded in the audit trail.
            </p>
            <textarea value={voidReason} onChange={(e) => setVoidReason(e.target.value)}
                      placeholder="Reason (recommended for legal records)"
                      data-testid="void-agreement-reason"
                      className="mt-3 w-full border border-[hsl(var(--primary)/0.18)] rounded-lg p-3 text-sm bg-[hsl(var(--card))]" rows={2} />
            <div className="mt-4 flex justify-end gap-2">
              <button onClick={() => { setVoiding(null); setVoidReason(''); }}
                      data-testid="void-agreement-cancel"
                      className="px-4 py-2 rounded-full border border-[hsl(var(--primary)/0.2)] text-sm">Cancel</button>
              <button onClick={doVoid} data-testid="void-agreement-confirm"
                      className="px-5 py-2 rounded-full bg-red-600 text-white text-sm font-medium">Void agreement</button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
